import { useMemo } from "react";
import { CircleCheck, Plus } from "lucide-react";

import CustomButton from "@/components/desktop/button/CustomButton";
import useFilterStore from "@/stores/useFilterStore";
import { formatNumber } from "@/utils/numberFormat";
import { getUnitLabel } from "@/utils/unitFormatter";

import { buildManualCodeSlots, getFilledManualCodes, getManualCodeSlotCount, isUnitMeasurement, normalizeQuantityValue } from "./oa-product-consumption.utils";

import styles from "./modal-oa-product-consumption.module.scss";

const OAProductConsumptionLine = ({
	line,
	consumptionMode,
	onModeChange,
	manualCodes,
	extraSlots,
	onManualCodeChange,
	onAddSlot,
	onConsumeManual,
	onConsumeAuto,
	isConsuming,
}) => {
	const globalUnitMeasurement = useFilterStore((state) => state.filter?.unit_measurement || "kg_l");

	const renderQuantity = (quantity, unitMeasurement) => (
		<>
			{formatNumber(normalizeQuantityValue(quantity))} {getUnitLabel(unitMeasurement, globalUnitMeasurement)}
		</>
	);

	const pendingQuantity = normalizeQuantityValue(line.pending_quantity);
	const isLineFinished = pendingQuantity <= 0;
	const isUnitProduct = isUnitMeasurement(line.unit_measurement);
	const slotCount = getManualCodeSlotCount(line, extraSlots);
	const codeSlots = useMemo(() => buildManualCodeSlots(line, manualCodes, extraSlots), [line, manualCodes, extraSlots]);
	const filledCodes = getFilledManualCodes(codeSlots);
	const allSlotsFilled = filledCodes.length === slotCount && slotCount > 0;
	const canAddWeightSlot = !isUnitProduct && slotCount < Math.max(1, line.allocations?.length || 1, Math.ceil(pendingQuantity));
	const canUseAuto = line.can_consume && line.allocations.length > 0;
	const activeMode = canUseAuto ? consumptionMode || "auto" : "manual";
	const isManualMode = activeMode === "manual";
	const canConsumeManual = filledCodes.length > 0 && (!isUnitProduct || allSlotsFilled);

	const statusMessage = isLineFinished
		? "Sin pendiente según historial activo."
		: isManualMode && isUnitProduct && !allSlotsFilled
			? `Ingresa ${slotCount} código${slotCount === 1 ? "" : "s"}.`
			: isManualMode
				? "Ingresa los códigos a dar de baja."
				: line.message;

	return (
		<div className={`${styles.lineRow} ${isLineFinished ? styles.lineFinished : ""}`}>
			<div className={styles.productBlock}>
				<strong>{line.product_name}</strong>
				<span>{renderQuantity(line.planned_quantity, line.unit_measurement)} planificado</span>
			</div>

			<div className={styles.quantityGrid}>
				<div>
					<span>Pendiente</span>
					<strong>{renderQuantity(line.pending_quantity, line.unit_measurement)}</strong>
				</div>
				<div>
					<span>Disponible</span>
					<strong>{renderQuantity(line.available_quantity, line.unit_measurement)}</strong>
				</div>
			</div>

			<div className={styles.codesBlock}>
				{isLineFinished ? null : (
					<>
						{canUseAuto ? (
							<div className={styles.modeToggle} role="tablist" aria-label="Modo de consumo">
								<button
									type="button"
									role="tab"
									aria-selected={!isManualMode}
									className={!isManualMode ? styles.modeToggleActive : ""}
									onClick={() => onModeChange(line.detail_id, "auto")}
									disabled={isConsuming}
								>
									Automático
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={isManualMode}
									className={isManualMode ? styles.modeToggleActive : ""}
									onClick={() => onModeChange(line.detail_id, "manual")}
									disabled={isConsuming}
								>
									Manual
								</button>
							</div>
						) : null}

						{isManualMode ? (
							<div className={styles.manualCodesRow}>
								{codeSlots.map((codeValue, slotIndex) => (
									<label key={`${line.detail_id}-slot-${slotIndex}`} className={styles.manualCodeField}>
										<span>{slotIndex + 1}</span>
										<input
											type="text"
											value={codeValue}
											onChange={(event) => onManualCodeChange(line.detail_id, slotIndex, event.target.value)}
											placeholder="Código"
											disabled={isConsuming}
											autoComplete="off"
										/>
									</label>
								))}
								{canAddWeightSlot ? (
									<button
										type="button"
										className={styles.addCodeButton}
										onClick={() => onAddSlot(line.detail_id)}
										disabled={isConsuming}
										title="Agregar código"
									>
										<Plus size={14} />
									</button>
								) : null}
							</div>
						) : (
							<div className={styles.suggestedRow}>
								{line.allocations.map((allocation) => (
									<span key={`${line.detail_id}-${allocation.inventory_id}`} className={styles.codePill}>
										{allocation.code} ({renderQuantity(allocation.quantity, line.unit_measurement)})
									</span>
								))}
							</div>
						)}
					</>
				)}
			</div>

			<div className={styles.actionBlock}>
				{isLineFinished ? (
					<span className={styles.finishedCheck}>
						<CircleCheck size={15} />
						Producto consumido
					</span>
				) : (
					<>
						{isManualMode ? (
							<CustomButton variant="primary" onClick={() => onConsumeManual(line)} disabled={isConsuming || !canConsumeManual}>
								Dar de baja códigos
							</CustomButton>
						) : (
							<CustomButton variant="primary" onClick={() => onConsumeAuto(line)} disabled={isConsuming || !canUseAuto}>
								Consumir producto
							</CustomButton>
						)}
						<span className={isManualMode && !canConsumeManual ? styles.unavailableMessage : styles.availableMessage}>{statusMessage}</span>
					</>
				)}
			</div>
		</div>
	);
};

export default OAProductConsumptionLine;
